import { useEffect, useState } from 'react';
import { sevilleHour } from '../lib/format.ts';
import { load, save } from '../lib/storage.ts';

export type ThemeMode = 'auto' | 'light' | 'dark';

/**
 * Light (readable in the sun) or dark (the Madrugá). "auto" follows the device, and
 * turns dark at night in Seville (21:00–07:00) by the server clock, so a simulated
 * night is dark too.
 */
export function useTheme(serverNow: string | null) {
  const [mode, setMode] = useState<ThemeMode>(() => load<ThemeMode>('cruz.theme', 'auto'));
  const [systemDark, setSystemDark] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const hour = serverNow ? sevilleHour(new Date(serverNow)) : null;
  const night = hour !== null && (hour >= 21 || hour < 7);
  const dark = mode === 'dark' || (mode === 'auto' && (systemDark || night));

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', dark ? '#221a27' : '#482052');
  }, [dark]);

  const cycle = () => {
    const next: ThemeMode = mode === 'auto' ? 'light' : mode === 'light' ? 'dark' : 'auto';
    setMode(next);
    save('cruz.theme', next);
  };

  return { mode, dark, cycle };
}
